import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Loader2, X, Send, Paperclip, Eye, Plus, Upload } from "lucide-react";
import PackFilePreview from "@/pages/collections/PackFilePreview";
import {
  listNoticeTemplates, getCaseNoticeContext, getCaseContactEmails, getDirectory,
  getPackFiles, uploadPackFile, sendAgeingM365Email, previewChaserEmailForCase,
} from "@/lib/api";

const fmtExpiry = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};
const fmtKb = (b) => (b ? (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`) : "");

// Inline composer for issuing formal notices (PDN / DN / Redemption / Settlement…)
// from a case file — template-driven email + case-file attachments + CC controls.
export default function IssueDocumentComposer({ caseRow, mailbox = "accounts", borrowerEmail, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [pickedId, setPickedId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [expiry, setExpiry] = useState("");
  const [expirySource, setExpirySource] = useState("");
  const [to, setTo] = useState(borrowerEmail || "");
  const [cc, setCc] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null); // { name, key }
  const [preview, setPreview] = useState({ subject: "", body: "", unsupported: [] });
  const [sending, setSending] = useState(false);
  const uploadRef = useRef(null);

  const extraTokens = { expiry_date: expiry ? fmtExpiry(expiry) : "" };
  const needsExpiry = `${subject}\n${body}`.includes("{expiry_date}");
  const pickedTemplate = templates.find((t) => t.template_id === pickedId);

  useEffect(() => { if (borrowerEmail) setTo((prev) => prev || borrowerEmail); }, [borrowerEmail]);

  useEffect(() => {
    (async () => {
      const [tpl, ctx, cts, dir] = await Promise.allSettled([
        listNoticeTemplates({ onlyActive: true }),
        getCaseNoticeContext(caseRow.case_id),
        getCaseContactEmails(caseRow.case_id),
        getDirectory(),
      ]);
      if (tpl.status === "fulfilled") setTemplates(tpl.value.templates || []);
      if (ctx.status === "fulfilled" && ctx.value.suggested_expiry) {
        setExpiry(ctx.value.suggested_expiry);
        setExpirySource(ctx.value.expiry_source || "");
      }
      if (cts.status === "fulfilled") setContacts(cts.value?.contacts || []);
      if (dir.status === "fulfilled") setUsers((dir.value || []).filter((u) => u.email));
    })();
    (async () => {
      try {
        const r = await getPackFiles(caseRow.case_id);
        setFiles(r.files || []);
      } catch { setFiles([]); } finally { setFilesLoading(false); }
    })();
  }, [caseRow.case_id]);

  // Debounced live preview — hydrates {tokens} (incl. {expiry_date}) server-side
  useEffect(() => {
    if (!subject.trim() && !body.trim()) { setPreview({ subject: "", body: "", unsupported: [] }); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await previewChaserEmailForCase(caseRow.case_id, { subject, body, extraTokens });
        if (!cancelled) setPreview({ subject: r.subject || "", body: r.body || "", unsupported: r.unsupported_tokens || [] });
      } catch { /* non-fatal */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [subject, body, expiry, caseRow.case_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickTemplate = (id) => {
    const t = templates.find((x) => x.template_id === id);
    if (!t) return;
    setPickedId(id);
    setSubject(t.subject || "");
    setBody(t.body || "");
    toast.success(`Loaded "${t.name}" — placeholders fill in automatically on send`);
  };

  const addCc = (email) => {
    const e = String(email || "").trim();
    if (!e || !e.includes("@")) return;
    if (cc.some((x) => x.toLowerCase() === e.toLowerCase())) return;
    setCc((prev) => [...prev, e]);
  };

  const toggleFile = (key) => setSelectedKeys((prev) => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const openPreview = (f) => setPreviewFile({ name: f.name, key: f.key });

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadPackFile(caseRow.case_id, file);
      const row = { key: `up:${r.upload_id}`, name: r.name, size: r.size, mime_type: r.mime_type, origin: "Uploaded" };
      setFiles((prev) => [row, ...prev]);
      setSelectedKeys((prev) => new Set(prev).add(row.key));
      toast.success(`${r.name} uploaded & attached`);
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const handleSend = async () => {
    if (!to.trim()) { toast.error("Add a recipient."); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required."); return; }
    if (needsExpiry && !expiry) { toast.error("Set the expiry date — the template references {expiry_date}."); return; }
    setSending(true);
    try {
      const r = await sendAgeingM365Email(caseRow.case_id, {
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc.length ? cc : null,
        subject, body, content_type: "HTML", actor: "Ops", mailbox,
        extra_tokens: extraTokens,
        attachment_keys: [...selectedKeys],
        notice_name: pickedTemplate?.name || "Document",
      });
      toast.success(`${pickedTemplate?.name || "Document"} issued to ${to}${r.attached ? ` · ${r.attached} attachment(s)` : ""}`);
      await onSent();
    } catch (e) {
      toast.error("Send failed: " + (e.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  const availableContacts = contacts.filter((c) =>
    !cc.some((x) => x.toLowerCase() === c.email.toLowerCase()) &&
    c.email.toLowerCase() !== to.trim().toLowerCase());
  const availableUsers = users.filter((u) => !cc.some((x) => x.toLowerCase() === (u.email || "").toLowerCase()));

  return (
    <div className="p-4 space-y-3" data-testid="issue-document-composer">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4 text-rose-600" /> Issue document
        </h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="issue-doc-close"><X className="w-4 h-4" /></button>
      </div>

      {/* Template + expiry */}
      <div className="rounded-md border border-rose-200 bg-rose-50/40 p-2 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Notice template</label>
          <Select value={pickedId} onValueChange={pickTemplate}>
            <SelectTrigger className="h-7 text-[11px] w-[260px] bg-white" data-testid="issue-doc-template-picker">
              <SelectValue placeholder="— Pick a notice template —" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.template_id} value={t.template_id} className="text-xs">{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[10px] text-muted-foreground">Manage templates in Portal Settings → UK → Notice Templates</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Notice expiry</label>
          <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
            className="h-7 text-[11px] w-[150px] bg-white" data-testid="issue-doc-expiry-input" />
          {expiry && <Badge variant="outline" className="text-[10px] bg-white">{fmtExpiry(expiry)}</Badge>}
          {expirySource && (
            <span className="text-[10px] text-emerald-700">✓ pulled from the {expirySource.replace(/_/g, " ")} date on the Legal Calendar</span>
          )}
          {needsExpiry && !expiry && (
            <span className="text-[10px] text-rose-700 font-medium">Template uses {"{expiry_date}"} — set the date</span>
          )}
        </div>
      </div>

      {/* To + CC */}
      <div>
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">To</label>
        <div className="flex items-center gap-1">
          <Input value={to} onChange={(e) => setTo(e.target.value)}
            placeholder="recipient@example.com (comma-separated for multiple)"
            className="h-8 text-xs flex-1" data-testid="issue-doc-to" />
          {contacts.length > 0 && (
            <Select value="" onValueChange={(v) => setTo(v)}>
              <SelectTrigger className="h-8 text-[10px] w-[130px]"><SelectValue placeholder="from case…" /></SelectTrigger>
              <SelectContent>
                {contacts.map((c, i) => (
                  <SelectItem key={i} value={c.email} className="text-xs">{c.label} · {c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">CC</label>
        <div className="flex flex-wrap items-center gap-1" data-testid="issue-doc-cc-row">
          {cc.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 border rounded-full px-2 py-0.5" data-testid={`issue-doc-cc-${e}`}>
              {e}
              <button onClick={() => setCc((prev) => prev.filter((x) => x !== e))} className="text-slate-400 hover:text-rose-600" data-testid={`issue-doc-cc-remove-${e}`}><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
          {availableContacts.length > 0 && (
            <Select value="" onValueChange={addCc}>
              <SelectTrigger className="h-6 text-[10px] w-[120px]" data-testid="issue-doc-cc-contact-select"><SelectValue placeholder="+ case contact" /></SelectTrigger>
              <SelectContent>
                {availableContacts.map((c, i) => (
                  <SelectItem key={i} value={c.email} className="text-xs">{c.label} · {c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {availableUsers.length > 0 && (
            <Select value="" onValueChange={addCc}>
              <SelectTrigger className="h-6 text-[10px] w-[120px]" data-testid="issue-doc-cc-user-select"><SelectValue placeholder="+ internal user" /></SelectTrigger>
              <SelectContent>
                {availableUsers.map((u) => (
                  <SelectItem key={u.id} value={u.email} className="text-xs">{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input value={ccInput} onChange={(e) => setCcInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { addCc(ccInput); setCcInput(""); } }}
            placeholder="type an email + Enter" className="h-6 text-[10px] w-[170px]" data-testid="issue-doc-cc-free-input" />
        </div>
      </div>

      <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
        className="h-9 text-xs" data-testid="issue-doc-subject" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9}
        placeholder="Pick a template above, or write the notice email here — {placeholders} fill in automatically"
        className="w-full px-3 py-2 border rounded-md text-xs font-mono focus:ring-rose-500 focus:border-rose-500"
        data-testid="issue-doc-body" />

      {/* Attachments */}
      <div className="rounded-md border p-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> Attachments ({selectedKeys.size} selected)
          </span>
          <label className="inline-flex items-center gap-1 text-[10px] text-blue-700 cursor-pointer hover:underline">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            Upload a file
            <input ref={uploadRef} type="file" className="hidden" data-testid="issue-doc-upload-input"
              onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} />
          </label>
        </div>
        {filesLoading ? (
          <div className="text-[11px] text-muted-foreground py-2 text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading case files…</div>
        ) : files.length === 0 ? (
          <div className="text-[11px] text-muted-foreground italic py-1">No files on this case yet — upload one above.</div>
        ) : (
          <div className="border rounded divide-y max-h-44 overflow-y-auto bg-white">
            {files.map((f) => (
              <div key={f.key} className="flex items-center gap-2 px-2 py-1 text-[11px] hover:bg-slate-50">
                <input type="checkbox" checked={selectedKeys.has(f.key)} onChange={() => toggleFile(f.key)}
                  data-testid={`issue-doc-file-${f.key.replace(/[^a-zA-Z0-9]/g, "-")}`} />
                <span className="truncate flex-1" title={f.name}>{f.name}</span>
                <Badge variant="outline" className="text-[9px] shrink-0">{f.origin}</Badge>
                {f.size ? <span className="text-[9px] text-muted-foreground shrink-0">{fmtKb(f.size)}</span> : null}
                <button onClick={() => openPreview(f)} title="Preview" className="text-slate-400 hover:text-blue-600 shrink-0" data-testid={`issue-doc-preview-${f.key.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email preview */}
      {(preview.subject || preview.body) && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-2 space-y-1" data-testid="issue-doc-preview-pane">
          <div className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold">As the customer will see it</div>
          {preview.unsupported.length > 0 && (
            <div className="text-[10px] text-rose-700">⚠ Unknown placeholder(s): {preview.unsupported.map((t) => `{${t}}`).join(", ")}</div>
          )}
          <div className="text-xs font-semibold">{preview.subject}</div>
          <div className="text-[11px] whitespace-pre-wrap text-slate-700 max-h-40 overflow-y-auto">{preview.body}</div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={handleSend} disabled={sending} className="bg-rose-700 hover:bg-rose-800" data-testid="issue-doc-send-btn">
          {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
          {sending ? "Sending…" : `Issue ${pickedTemplate?.name || "document"}${selectedKeys.size ? ` + ${selectedKeys.size} file(s)` : ""}`}
        </Button>
      </div>

      {previewFile && (
        <Dialog open onOpenChange={(v) => { if (!v) setPreviewFile(null); }}>
          <DialogContent className="max-w-4xl h-[85vh]" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="text-sm truncate">{previewFile.name}</DialogTitle></DialogHeader>
            <PackFilePreview
              caseId={caseRow.case_id}
              fileKey={previewFile.key}
              name={previewFile.name}
              frameClassName="w-full h-full border rounded"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
